/**
 * Utility functions for working with multiple price types
 */

/**
 * Get the price for a product given a store and price type
 * @param {Object} product - The product object
 * @param {string} storeId - The store ID
 * @param {string} priceTypeId - The price type ID (e.g., 'wholesale', 'bnpl') or null for default
 * @param {string} scope - The scope ('store' or 'organization') - optional, will check priceTypeScopes if not provided
 * @param {Array} priceTypes - Array of price type configurations for auto-calculation
 * @returns {number} The price or 0 if not found
 */
export const getProductPrice = (product, storeId, priceTypeId, scope, priceTypes) => {
  if (!product || !product.salePrices) return 0
  
  // If no priceTypeId specified, use default store price
  if (!priceTypeId || priceTypeId === 'default') {
    const price = product.salePrices[storeId]
    // Ensure we return a valid number, not NaN or undefined
    return price && !isNaN(price) ? price : 0
  }
  
  // Determine scope from priceTypeScopes if not provided
  if (!scope) {
    scope = product.priceTypeScopes?.[priceTypeId] || 'store'
  }
  
  // Build the key based on scope
  const key = scope === 'organization' ? `org_${priceTypeId}` : `${storeId}_${priceTypeId}`
  
  // Check if price already exists
  const existingPrice = product.salePrices[key]
  
  // If priceTypes provided, check for calculation rules
  if (priceTypes && priceTypes.length > 0) {
    const priceType = priceTypes.find(pt => pt.id === priceTypeId)
    if (priceType?.calculationRules?.enabled) {
      const defaultPrice = product.salePrices[storeId] || 0
      if (defaultPrice > 0) {
        const calculatedPrice = calculatePriceFromDefault(defaultPrice, priceType.calculationRules)
        // Ensure we return a valid number, not NaN or undefined
        return calculatedPrice && !isNaN(calculatedPrice) ? calculatedPrice : 0
      }
    }
  }

  if (existingPrice !== undefined && existingPrice !== null && !isNaN(existingPrice)) {
    return existingPrice
  }
  
  // Fall back to default price, ensuring it's a valid number
  const fallbackPrice = product.salePrices[storeId]
  return fallbackPrice && !isNaN(fallbackPrice) ? fallbackPrice : 0
}

/**
 * Whether the product carries an explicit finite value that the selected pricing path can use.
 * Unlike getProductPrice, this preserves the difference between an intentional 0 and a missing
 * key — checkout must allow the former and refuse the latter before it is written as a zero sale.
 */
export const hasResolvableProductPrice = (product, storeId, priceTypeId) => {
  const salePrices = product?.salePrices
  const flatPrice = product?.salePrice ?? product?.price
  const hasFiniteFlatPrice = typeof flatPrice === 'number' && Number.isFinite(flatPrice)
  if (!salePrices || typeof salePrices !== 'object') return hasFiniteFlatPrice

  const hasFinite = (key) => Object.prototype.hasOwnProperty.call(salePrices, key)
    && typeof salePrices[key] === 'number'
    && Number.isFinite(salePrices[key])

  if (!priceTypeId || priceTypeId === 'default') return hasFinite(storeId) || hasFiniteFlatPrice

  const scope = product.priceTypeScopes?.[priceTypeId] || 'store'
  const selectedKey = scope === 'organization' ? `org_${priceTypeId}` : `${storeId}_${priceTypeId}`
  if (hasFinite(selectedKey)) return true

  // getProductPrice deliberately falls back to the default price when a tier has no explicit
  // value (and also derives calculated tiers from it). The safety check must accept that same
  // path rather than rejecting a sale whose total calculation is valid.
  return hasFinite(storeId) || hasFiniteFlatPrice
}

/**
 * Get available price types for a product at a given store
 * @param {Object} product - The product object
 * @param {string} storeId - The store ID
 * @param {Array} organizationPriceTypes - Array of price type configurations from organization constants
 * @returns {Array} Array of available price type objects with prices
 */
export const getAvailablePriceTypes = (product, storeId, organizationPriceTypes = []) => {
  if (!product || !organizationPriceTypes || organizationPriceTypes.length === 0) {
    return []
  }
  
  return organizationPriceTypes
    .map(priceType => {
      const price = getProductPrice(product, storeId, priceType.id)
      return {
        ...priceType,
        price,
        available: price > 0
      }
    })
    .filter(pt => pt.available)
}

/**
 * Get the default price object (represents salePrices)
 * @returns {Object} Default price type object
 */
export const getDefaultPriceType = () => {
  return {
    id: null,
    name: 'Default',
    isDefault: true
  }
}

/**
 * Determine which price type should be auto-selected based on rules
 * @param {Array} cartItems - Current cart items
 * @param {Object} paymentMethod - Selected payment method
 * @param {Object} client - Selected client (optional)
 * @param {Array} priceTypes - Array of price type configurations (additional prices only)
 * @param {number} cartTotal - Total cart amount (optional, calculated from cartItems if not provided)
 * @returns {string|null} The suggested price type ID or null for default
 */
export const suggestPriceType = (cartItems = [], paymentMethod = null, client = null, priceTypes = [], cartTotal = null) => {
  // Check client preference first
  if (client?.preferredPriceType) {
    const clientPriceType = priceTypes.find(pt => pt.id === client.preferredPriceType)
    if (clientPriceType) {
      return client.preferredPriceType
    }
  }
  
  // Calculate cart total if not provided
  if (cartTotal === null && cartItems && cartItems.length > 0) {
    cartTotal = cartItems.reduce((sum, item) => {
      const itemPrice = item.price || 0
      const itemQuantity = item.quantity || 0
      return sum + (itemPrice * itemQuantity)
    }, 0)
  }
  
  // Check quantity-based rules
  const totalQuantity = cartItems && Array.isArray(cartItems) ? cartItems.reduce((sum, item) => sum + (item.quantity || 0), 0) : 0
  const quantityBasedTypes = priceTypes
    .filter(pt => pt.autoRules?.rules)
    .flatMap(pt => 
      pt.autoRules.rules
        .filter(rule => rule.type === 'quantity')
        .map(rule => ({
          priceTypeId: pt.id,
          minQuantity: rule.minQuantity || 0
        }))
    )
    .sort((a, b) => b.minQuantity - a.minQuantity)
    
  for (const item of quantityBasedTypes) {
    if (totalQuantity >= item.minQuantity) {
      return item.priceTypeId
    }
  }
  
  // Check cart total rules
  const cartTotalBasedTypes = priceTypes
    .filter(pt => pt.autoRules?.rules)
    .flatMap(pt => 
      pt.autoRules.rules
        .filter(rule => rule.type === 'cart_total')
        .map(rule => ({
          priceTypeId: pt.id,
          minAmount: rule.minAmount || 0
        }))
    )
    .sort((a, b) => b.minAmount - a.minAmount)
  
  for (const item of cartTotalBasedTypes) {
    if (cartTotal >= item.minAmount) {
      return item.priceTypeId
    }
  }
  
  // Check payment method rules
  if (paymentMethod) {
    for (const priceType of priceTypes) {
      if (!priceType.autoRules?.rules) continue
      
      const paymentRule = priceType.autoRules.rules.find(rule => 
        rule.type === 'payment_method' &&
        rule.paymentMethodIds?.includes(paymentMethod.id)
      )
      
      if (paymentRule) {
        return priceType.id
      }
    }
  }
  
  // Return null for default pricing (salePrices)
  return null
}


/**
 * Set a price for a product at a specific price type and store
 * All prices stored in salePrices with key convention
 * @param {Object} product - The product object
 * @param {string} storeId - The store ID
 * @param {string} priceTypeId - The price type ID (null for default)
 * @param {number|string} price - The price to set
 * @param {string} scope - 'store' or 'organization'
 * @returns {Object} Updated product object
 */
export const setProductPrice = (product, storeId, priceTypeId, price, scope = 'store') => {
  const updatedProduct = { ...product }
  
  // Initialize salePrices if it doesn't exist
  if (!updatedProduct.salePrices) {
    updatedProduct.salePrices = {}
  }
  
  // Parse value
  const numValue = typeof price === 'string' ? parseFloat(price) || 0 : price
  
  // If no priceTypeId, set default price
  if (!priceTypeId || priceTypeId === 'default') {
    updatedProduct.salePrices[storeId] = numValue
  } else {
    // Build the key based on scope
    const key = scope === 'organization' ? `org_${priceTypeId}` : `${storeId}_${priceTypeId}`
    updatedProduct.salePrices[key] = numValue
    
    // Update scope configuration
    if (!updatedProduct.priceTypeScopes) {
      updatedProduct.priceTypeScopes = {}
    }
    updatedProduct.priceTypeScopes[priceTypeId] = scope
  }
  
  return updatedProduct
}

/**
 * Copy prices from one store to another for a specific price type
 * @param {Object} product - The product object
 * @param {string} fromStoreId - Source store ID
 * @param {string} toStoreId - Destination store ID
 * @param {string} priceTypeId - The price type to copy (or null for default)
 * @param {string} scope - The scope of the price type
 * @returns {Object} Updated product object
 */
export const copyPriceBetweenStores = (product, fromStoreId, toStoreId, priceTypeId = null, scope = 'store') => {
  const updatedProduct = { ...product }
  
  if (!updatedProduct.salePrices) {
    return updatedProduct
  }
  
  // Get the source price
  const fromPrice = getProductPrice(product, fromStoreId, priceTypeId, scope)
  
  if (!fromPrice) {
    return updatedProduct
  }
  
  // Set to destination using the same logic
  return setProductPrice(updatedProduct, toStoreId, priceTypeId, fromPrice, scope)
}

/**
 * Initialize priceTypeScopes structure for a product
 * Note: All prices (default and additional) are stored in salePrices
 * @param {Object} product - The product object
 * @returns {Object} Updated product object with priceTypeScopes initialized
 */
export const initializePriceTypeScopes = (product) => {
  if (!product) {
    return product
  }
  
  const updatedProduct = { ...product }
  
  // Initialize priceTypeScopes for tracking scope configuration
  if (!updatedProduct.priceTypeScopes) {
    updatedProduct.priceTypeScopes = {}
  }
  
  return updatedProduct
}

/**
 * Calculate price based on default price and calculation rules
 * @param {number} defaultPrice - The default/base price
 * @param {Object} calculationRules - Calculation configuration
 * @returns {number} Calculated price
 */
export const calculatePriceFromDefault = (defaultPrice, calculationRules) => {
  if (!calculationRules || !calculationRules.enabled) {
    return 0
  }
  
  let price = parseFloat(defaultPrice) || 0
  
  if (calculationRules.type === 'multiply') {
    price = price * (calculationRules.multiplier || 1)
  } else if (calculationRules.type === 'adjust') {
    price = price + (calculationRules.amount || 0)
  } else if (calculationRules.type === 'percentage') {
    price = price * (1 + (calculationRules.percentage || 0) / 100)
  }
  
  return Math.round(price * 100) / 100 // Round to 2 decimals
}

/**
 * Apply auto-calculation to all price types that have it enabled
 * Uses salePrices[storeId] as the base/default price for calculations
 * @param {Object} product - The product object
 * @param {Array} priceTypes - Array of price type configurations
 * @param {string} storeId - The store ID
 * @returns {Object} Updated product with calculated prices
 */
export const applyPriceCalculations = (product, priceTypes, storeId) => {
  const updatedProduct = { ...product }
  
  // Get default price from salePrices (base pricing)
  const defaultPrice = product.salePrices?.[storeId] || 0
  
  if (!defaultPrice) {
    return updatedProduct
  }
  
  // Initialize salePrices if needed
  if (!updatedProduct.salePrices) {
    updatedProduct.salePrices = {}
  }
  
  // Initialize priceTypeScopes if needed
  if (!updatedProduct.priceTypeScopes) {
    updatedProduct.priceTypeScopes = {}
  }
  
  // Calculate prices for additional price types with auto-calculation enabled
  priceTypes.forEach(priceType => {
    if (priceType.calculationRules?.enabled) {
      const calculatedPrice = calculatePriceFromDefault(defaultPrice, priceType.calculationRules)
      const scope = priceType.scope || 'store'
      
      // Store with key convention
      const key = scope === 'organization' ? `org_${priceType.id}` : `${storeId}_${priceType.id}`
      updatedProduct.salePrices[key] = calculatedPrice
      
      // Track scope
      updatedProduct.priceTypeScopes[priceType.id] = scope
    }
  })
  
  return updatedProduct
}

/**
 * Get default price types configuration for a new organization
 * Note: This returns empty array as salePrices are the default
 * Additional price types are optional and configured as needed
 * @returns {Array} Default price types (empty - salePrices are default)
 */
export const getDefaultPriceTypesConfig = () => {
  return []
}
